import Cookies from 'js-cookie';
import {
    ONETRUST_CONSENT_CHANGED,
    ONETRUST_NATIVE_CONSENT_CHANGED,
    ONETRUST_TARGETING_COOKIES_GROUP,
} from '../../model/const/onetrust';
import { PERSONALIZATION_COOKIE_DEMANDBASE } from '../../model/const/personalization';
import { Logger } from '../../utils/logger';
import {
    type DemandbaseCompany,
    getDemandbaseSegments,
    resolveDemandbasePersonalization,
} from './utils-demandbase-personalization';

const DEMANDBASE_API_URL = 'https://api.company-target.com/api/v3/ip.json';
const DEMANDBASE_API_TOKEN = '8Q5mw7QNp7mClrqk33U55VcRoXYQ7nLGnWGIWwex';
const REQUEST_TIMEOUT_MS = 10_000;
const COOKIE_LIFETIME_MONTHS = 3;
const COOKIE_ATTRIBUTES = { path: '/', domain: '.acronis.com' };

let started = false;

// `null` until OneTrust has loaded and exposed the active groups
function getTargetingConsent(): boolean | null {
    const groups = window.OptanonActiveGroups;
    return typeof groups === 'string' ? groups.includes(ONETRUST_TARGETING_COOKIES_GROUP) : null;
}

async function fetchCompany(): Promise<DemandbaseCompany> {
    const url = new URL(DEMANDBASE_API_URL);
    url.searchParams.set('auth', DEMANDBASE_API_TOKEN);
    url.searchParams.set('page', window.location.href);
    url.searchParams.set('page_title', document.title);
    url.searchParams.set('referrer', document.referrer);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    try {
        const res = await fetch(url, { cache: 'no-store', signal: controller.signal });
        if (!res.ok) {
            throw new Error(`Demandbase responded with ${res.status}`);
        }

        const data: unknown = await res.json();
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
            throw new Error('Demandbase responded with invalid payload');
        }

        return data;
    } finally {
        clearTimeout(timeout);
    }
}

function applyCookie(company: DemandbaseCompany) {
    const audience = resolveDemandbasePersonalization(company);

    if (!audience) {
        Cookies.remove(PERSONALIZATION_COOKIE_DEMANDBASE, COOKIE_ATTRIBUTES);
        return;
    }

    const expires = new Date();
    expires.setMonth(expires.getMonth() + COOKIE_LIFETIME_MONTHS);

    Cookies.set(PERSONALIZATION_COOKIE_DEMANDBASE, audience, {
        ...COOKIE_ATTRIBUTES,
        expires,
        secure: true,
        sameSite: 'Lax',
    });
}

async function runDemandbase() {
    try {
        const company = await fetchCompany();

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'demandbase_resolved',
            demandbase_company: company,
            demandbase_segments: getDemandbaseSegments(company),
        });

        applyCookie(company);
    } catch (e) {
        Logger.error(e, 'Demandbase failed');
    }
}

function onConsentChanged() {
    const consent = getTargetingConsent();

    if (consent === false) {
        Cookies.remove(PERSONALIZATION_COOKIE_DEMANDBASE, COOKIE_ATTRIBUTES);
        return;
    }

    if (consent && !started) {
        started = true;
        runDemandbase();
    }
}

export function initDemandbase() {
    window.addEventListener(ONETRUST_CONSENT_CHANGED, onConsentChanged);
    window.addEventListener(ONETRUST_NATIVE_CONSENT_CHANGED, onConsentChanged);
    onConsentChanged();
}
